import { Controller } from '@hotwired/stimulus'

export default class extends Controller {
  static targets = ['download', 'newReport', 'progress', 'start', 'stop']
  static values = { endAt: String, runAt: String, startAt: String, url: String }

  connect() {
    this.records = []
    this.cursor = null
    this.summary = {candidate_count: 0, excluded_count: 0, classified_count: 0, errored_count: 0, total_count: 0}
    this.done = false
    this.halted = false
    this.haltReason = null
    this.requestError = null
    this.running = false
    this.execution = 0
    this.storageKey = 'pipe17-stranded-order-report:active'
    this.restore()
  }

  start() {
    if (this.running || this.done) return

    this.running = true
    this.runToken = ++this.execution
    this.startTarget.disabled = true
    this.newReportTarget.disabled = true
    this.stopTarget.disabled = false
    this.downloadTarget.disabled = false
    this.persistOrStop()
    this.next(this.runToken)
  }

  stop() {
    this.running = false
    this.execution += 1
    window.clearTimeout(this.nextTimer)
    this.stopTarget.disabled = true
    this.startTarget.disabled = this.done || this.halted
    this.newReportTarget.disabled = false
    this.progressTarget.textContent = `Stopped after ${this.records.length} orders. Download the partial report.`
  }

  newReport() {
    if (this.running) return

    window.clearTimeout(this.nextTimer)
    this.execution += 1
    sessionStorage.removeItem(this.storageKey)
    this.runAtValue = new Date().toISOString()
    this.records = []
    this.cursor = null
    this.summary = {candidate_count: 0, excluded_count: 0, classified_count: 0, errored_count: 0, total_count: 0}
    this.done = false
    this.halted = false
    this.haltReason = null
    this.requestError = null
    this.startTarget.disabled = false
    this.stopTarget.disabled = true
    this.downloadTarget.disabled = true
    this.progressTarget.textContent = 'Ready.'
  }

  async next() {
    if (!this.running || arguments[0] !== this.execution) return

    try {
      const response = await fetch(this.urlValue, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content,
        },
        credentials: 'same-origin',
        body: JSON.stringify({
          run_at: this.runAtValue,
          start_at: this.startAtValue,
          end_at: this.endAtValue,
          cursor_charged_at: this.cursor?.charged_at,
          cursor_id: this.cursor?.id,
        }),
      })
      const result = await response.json()
      if (!this.running || arguments[0] !== this.execution) return
      if (!response.ok) throw new Error(result.error || 'The report request failed')

      this.records.push(...result.records)
      this.cursor = result.next_cursor
      this.done = result.done
      this.halted = Boolean(result.halted)
      this.haltReason = result.halt_reason || null
      Object.keys(this.summary).forEach((key) => { this.summary[key] += result.summary[key] || 0 })
      if (!this.persistOrStop()) return

      if (this.halted) {
        this.running = false
        this.stopTarget.disabled = true
        this.newReportTarget.disabled = false
        this.progressTarget.textContent = `${this.haltReason}. Download the incomplete report.`
        return
      }

      this.progressTarget.textContent = `Processed ${this.records.length} orders.`
      if (result.done) {
        this.finish()
        return
      }

      const token = this.runToken
      this.nextTimer = window.setTimeout(() => this.next(token), 500)
    } catch (_error) {
      if (!this.running || arguments[0] !== this.execution) return

      this.running = false
      this.halted = true
      this.haltReason = 'Report request failed'
      this.requestError = {status: 'request_error', reason: this.haltReason}
      this.stopTarget.disabled = true
      this.newReportTarget.disabled = false
      if (this.persistOrStop()) {
        this.progressTarget.textContent = `${this.haltReason}. Download the incomplete report.`
      }
    }
  }

  finish() {
    this.running = false
    this.stopTarget.disabled = true
    this.newReportTarget.disabled = false
    this.startTarget.disabled = true
    this.progressTarget.textContent = `Completed ${this.records.length} orders. Download the report.`
  }

  persistOrStop() {
    try {
      sessionStorage.setItem(this.storageKey, JSON.stringify(this.artifact()))
      return true
    } catch (_error) {
      this.running = false
      this.stopTarget.disabled = true
      this.downloadTarget.disabled = false
      this.progressTarget.textContent = 'Browser storage is unavailable. Download the partial report now.'
      return false
    }
  }

  artifact() {
    const classificationCounts = this.records.reduce((counts, record) => ({...counts, [record.classification]: (counts[record.classification] || 0) + 1}), {})
    return {run_at: this.runAtValue, start_at: this.startAtValue, end_at: this.endAtValue, cursor: this.cursor, done: this.done, halted: this.halted, halt_reason: this.haltReason, request_error: this.requestError, summary: this.summary, classification_counts: classificationCounts, records: this.records}
  }

  restore() {
    try {
      const saved = JSON.parse(sessionStorage.getItem(this.storageKey))
      if (!saved || !saved.run_at || !saved.start_at || !saved.end_at) return
      this.runAtValue = saved.run_at
      this.startAtValue = saved.start_at
      this.endAtValue = saved.end_at
      this.records = saved.records || []
      this.cursor = saved.cursor || null
      this.summary = saved.summary || this.summary
      this.done = Boolean(saved.done)
      this.halted = Boolean(saved.halted)
      this.haltReason = saved.halt_reason || null
      this.requestError = saved.request_error || null
      this.downloadTarget.disabled = false
      this.stopTarget.disabled = true
      this.startTarget.disabled = this.done || this.halted
      this.progressTarget.textContent = this.halted ? `${this.haltReason}. Download the incomplete report.` : this.done ? `Completed ${this.records.length} orders. Download the report.` : `Restored ${this.records.length} orders. Resume the report or download the partial.`
    } catch (_error) {}
  }

  download() {
    const blob = new Blob([JSON.stringify(this.artifact(), null, 2)], {type: 'application/json'})
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `pipe17-stranded-order-report-${this.runAtValue.replace(/:/g, '-')}.json`
    link.click()
    URL.revokeObjectURL(url)
  }
}
